<template>
  <section class="section container">
    <div class="container mt-0 mb-6">
      <h1 class="title is-4 mb-3 is-pulled-right">
        {{ $t("debt level module settings") }}
      </h1>
    </div>

    <div class="box">
      <div class="title is-4">{{ $t("sib data segmentation") }}</div>
      <div class="subtitle is-5">
        {{ $t("this conditions are used to segment customer active types") }}
      </div>

      <div class="title is-5 mt-5">
        {{ $t("loan identification conditions") }}
      </div>
      <div class="columns">
        <div class="column is-10">
          <table class="table is-striped is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th>{{ $t("active type") }}</th>
                <th>{{ $t("warranty type") }}</th>
                <th>{{ $t("segment") }}</th>
                <th>{{ $t("actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(condition, idx) in meta.loan_conditions" :key="idx">
                <td>{{ condition.active_type }}</td>
                <td>{{ condition.warranty_type }}</td>
                <td>{{ condition.segment }}</td>
                <td>
                  <button
                    class="button is-small is-danger"
                    @click="meta.loan_conditions.splice(idx, 1)"
                  >
                    <span class="icon"><i class="fa fa-trash"></i></span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="column">
          <div
            class="button is-primary is-rounded"
            @click="add_condition('loan')"
          >
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t("add") }}</span>
          </div>
        </div>
      </div>

      <div class="title is-5 mt-5">
        {{ $t("credit card identification conditions") }}
      </div>
      <div class="columns">
        <div class="column is-10">
          <table class="table is-striped is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th>{{ $t("active type") }}</th>
                <th>{{ $t("warranty type") }}</th>
                <th>{{ $t("segment") }}</th>
                <th>{{ $t("actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(condition, idx) in meta.credit_card_conditions"
                :key="idx"
              >
                <td>{{ condition.active_type }}</td>
                <td>{{ condition.warranty_type }}</td>
                <td>{{ condition.segment }}</td>
                <td>
                  <button
                    class="button is-small is-danger"
                    @click="meta.credit_card_conditions.splice(idx, 1)"
                  >
                    <span class="icon"><i class="fa fa-trash"></i></span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="column">
          <div
            class="button is-primary is-rounded"
            @click="add_condition('credit_card')"
          >
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t("add") }}</span>
          </div>
        </div>
      </div>

      <div class="title is-5 mt-5">
        {{ $t("extrafinancing identification conditions") }}
      </div>
      <div class="columns">
        <div class="column is-10">
          <table class="table is-striped is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th>{{ $t("active type") }}</th>
                <th>{{ $t("warranty type") }}</th>
                <th>{{ $t("segment") }}</th>
                <th>{{ $t("actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(condition, idx) in meta.extrafinancing_conditions"
                :key="idx"
              >
                <td>{{ condition.active_type }}</td>
                <td>{{ condition.warranty_type }}</td>
                <td>{{ condition.segment }}</td>
                <td>
                  <button
                    class="button is-small is-danger"
                    @click="meta.extrafinancing_conditions.splice(idx, 1)"
                  >
                    <span class="icon"><i class="fa fa-trash"></i></span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="column">
          <div
            class="button is-primary is-rounded"
            @click="add_condition('extrafinancing')"
          >
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t("add") }}</span>
          </div>
        </div>
      </div>

      <div class="title is-5 mt-5">
        {{ $t("mortgage identification conditions") }}
      </div>
      <div class="columns">
        <div class="column is-10">
          <table class="table is-striped is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th>{{ $t("active type") }}</th>
                <th>{{ $t("warranty type") }}</th>
                <th>{{ $t("segment") }}</th>
                <th>{{ $t("actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(condition, idx) in meta.mortgage_conditions"
                :key="idx"
              >
                <td>{{ condition.active_type }}</td>
                <td>{{ condition.warranty_type }}</td>
                <td>{{ condition.segment }}</td>
                <td>
                  <button
                    class="button is-small is-danger"
                    @click="meta.mortgage_conditions.splice(idx, 1)"
                  >
                    <span class="icon"><i class="fa fa-trash"></i></span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="column">
          <div
            class="button is-primary is-rounded"
            @click="add_condition('mortgage')"
          >
            <span class="icon"><i class="fa fa-plus"></i></span>
            <span>{{ $t("add") }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="box">
      <div class="columns mt-5">
        <div class="column">
          <div class="field">
            <generic-dropdown
              :title="$t('sib data extraction script')"
              :placeholder="$t('select one')"
              :is_multiple="false"
              url="/script/catalog"
              name_field="name"
              value_field="directory"
              @selected="sib_extraction_script_changed"
              ref="script_dropdown"
            />
          </div>
        </div>
        <div class="column">
          <div class="field">
            <generic-dropdown
              :title="$t('report generation script')"
              :placeholder="$t('select one')"
              :is_multiple="false"
              url="/script/catalog"
              name_field="name"
              value_field="directory"
              @selected="sib_report_script_changed"
              ref="report_script_dropdown"
            />
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <div class="field">
            <generic-dropdown
              :title="$t('drive')"
              :placeholder="$t('select one')"
              :is_multiple="false"
              url="/drive/catalog"
              name_field="name"
              value_field="drive_id"
              @selected="drive_changed"
              ref="drive_dropdown"
            />
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("shared directory path") }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="meta.shared_directory_path"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="columns mt-5">
        <div class="column">
          <div class="field">
            <label class="label">{{
              $t("success endpoint url (extraction)")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="meta.success_endpoint_url_extraction"
              />
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{
              $t("error endpoint url (extraction)")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="meta.error_endpoint_url_extraction"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="columns mt-5">
        <div class="column">
          <div class="field">
            <label class="label">{{
              $t("success endpoint url (report)")
            }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="meta.success_endpoint_url_report"
              />
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("error endpoint url (report)") }}</label>
            <div class="control">
              <input
                class="input"
                type="text"
                v-model="meta.error_endpoint_url_report"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="box">
      <div class="title is-4">
        {{ $t("electronic signature database configuration") }}
      </div>
      <div class="columns mt-5">
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("host") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.electronic_signature_db_host"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-server"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("port") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.electronic_signature_db_port"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-plug"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("database") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.electronic_signature_db_name"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-database"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="columns mt-5">
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("username") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="text"
                v-model="meta.electronic_signature_db_username"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-user"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("password") }}</label>
            <div class="control has-icons-left has-icons-right">
              <input
                class="input"
                type="password"
                v-model="meta.electronic_signature_db_password"
              />
              <span class="icon is-small is-left">
                <i class="fa fa-lock"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">{{ $t("test") }}</label>
            <div class="control">
              <button
                class="button is-rounded is-primary"
                @click="test_electronic_signature_connection"
              >
                <span class="icon"><i class="fa fa-check"></i></span>
                <span>{{ $t("test connection") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container mt-6">
      <div class="field mt-5">
        <div class="control">
          <button class="button is-rounded is-primary" @click="save">
            <span class="icon"><i class="fa fa-save"></i></span>
            <span>{{ $t("save") }}</span>
          </button>
        </div>
      </div>
    </div>
    <condition-modal ref="condition_modal" @save="condition_saved" />
  </section>
</template>

<script>
import {
  save_configuration,
  get_configuration,
  post,
  api_host,
  error_message,
  success_message,
} from "@/utils";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";
import GenericUploader from "@/components/generic_uploader/Index.vue";
import ConditionModal from "./condition_modal/Index.vue";

export default {
  name: "DebtLevelSettings",
  components: { GenericDropdown, GenericUploader, ConditionModal },
  data() {
    return {
      meta: {},
      api_host,
    };
  },
  computed: {
    credit_card_identifier_array() {
      let v = this;
      return v.meta.credit_card_identifiers
        ? v.meta.credit_card_identifiers
            .split(",")
            .map((itm) => itm.trim())
            .filter((itm) => itm)
        : [];
    },
    loan_identifier_array() {
      let v = this;
      return v.meta.loan_identifiers
        ? v.meta.loan_identifiers
            .split(",")
            .map((itm) => itm.trim())
            .filter((itm) => itm)
        : [];
    },
    extrafinancing_identifier_array() {
      let v = this;
      return v.meta.extrafinancing_identifiers
        ? v.meta.extrafinancing_identifiers
            .split(",")
            .map((itm) => itm.trim())
            .filter((itm) => itm)
        : [];
    },
    mortgage_identifier_array() {
      let v = this;
      return v.meta.mortgage_identifiers
        ? v.meta.mortgage_identifiers
            .split(",")
            .map((itm) => itm.trim())
            .filter((itm) => itm)
        : [];
    },
  },
  methods: {
    save() {
      let v = this;
      let data = v.meta;
      v.$root.loader(true);
      save_configuration(this, data)
        .then(() => {
          success_message(v.$t("configuration saved"));
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => v.$root.loader(false));
    },
    sib_extraction_script_changed(script) {
      let v = this;
      if (script) {
        v.meta.sib_extraction_script = script;
      }
    },
    sib_report_script_changed(script) {
      let v = this;
      if (script) {
        v.meta.sib_report_script = script;
      }
    },
    drive_changed(drive) {
      let v = this;
      if (drive) {
        v.meta.drive_id = drive.drive_id;
      }
    },
    add_condition(type) {
      let v = this;
      v.$refs.condition_modal.create(type);
    },
    condition_saved(condition) {
      let v = this;
      if (condition) {
        if (condition.type === "loan") {
          v.meta.loan_conditions = v.meta.loan_conditions || [];
          v.meta.loan_conditions.push(condition);
        } else if (condition.type === "credit_card") {
          v.meta.credit_card_conditions = v.meta.credit_card_conditions || [];
          v.meta.credit_card_conditions.push(condition);
        } else if (condition.type === "extrafinancing") {
          v.meta.extrafinancing_conditions =
            v.meta.extrafinancing_conditions || [];
          v.meta.extrafinancing_conditions.push(condition);
        } else if (condition.type === "mortgage") {
          v.meta.mortgage_conditions = v.meta.mortgage_conditions || [];
          v.meta.mortgage_conditions.push(condition);
        }
      }
    },
    test_electronic_signature_connection() {
      let v = this;
      v.$root.loader(true);
      post("/debt-level/test-fe-connection", {
        host: v.meta.electronic_signature_db_host,
        port: v.meta.electronic_signature_db_port,
        database: v.meta.electronic_signature_db_name,
        username: v.meta.electronic_signature_db_username,
        password: v.meta.electronic_signature_db_password,
      })
        .then(() => {
          success_message(v.$t("connection successful"));
        })
        .catch((error) => {
          console.error(error);
          error_message(error);
        })
        .finally(() => v.$root.loader(false));
    },
  },
  created() {
    let v = this;
    v.$root.loader(true);
    get_configuration(this)
      .then((conf) => {
        if (conf) v.meta = conf;

        if (
          v.meta.sib_extraction_script &&
          v.meta.sib_extraction_script.directory
        )
          v.$refs.script_dropdown.set_value(
            v.meta.sib_extraction_script.directory
          );

        if (v.meta.sib_report_script && v.meta.sib_report_script.directory)
          v.$refs.report_script_dropdown.set_value(
            v.meta.sib_report_script.directory
          );

        if (v.meta.drive_id) v.$refs.drive_dropdown.set_value(v.meta.drive_id);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => v.$root.loader(false));
  },
};
</script>

<style></style>
